import { v4 as uuidv4 } from 'uuid';
import { omit } from 'lodash';
import { convertValue } from '@/utils/util-measurements';
import {
  CompressorData,
  PressureTypeRoomData,
  TanksType,
  Room,
  RoomConfigurationStepData,
  CustomerInformationStepData,
  SiteConfigurationStepData,
  AccountResponse,
  PressureTypeFull,
  SaveOnboardingFormPayload,
  CreateImagePayload,
  STATION_USAGE_TYPES,
  ImageData,
  COMPRESSOR_DATA,
  NO_MATCHING_SPEC,
  SaveRoomPayload,
  SaveCompressorDataPayload,
} from '../constants';
import { splitFullName } from './customerInformationStepAdapter';
import { convertSiteConfigurationStepDataToDTO } from './siteConfigurationStepAdapter';
import rootStore from '@/store/RootStore';

export const processRoomsData = (
  roomConfigurationStepData: Partial<RoomConfigurationStepData>,
  pressureType: string,
): Room[] => {
  const roomsData: SaveRoomPayload[] = [];
  const pressureTypeData = (roomConfigurationStepData[pressureType] as PressureTypeRoomData) || { rooms: [] };
  const rooms = pressureTypeData.rooms || [];

  rooms.forEach(room => {
    if (COMPRESSOR_DATA in room && Array.isArray(room.compressorData)) {
      const roomDataObj: SaveRoomPayload = {
        compressorsInRoom: Array.isArray(room.compressorsInRoom) ? room.compressorsInRoom : [],
        compressorData: Array.isArray(room.compressorData)
          ? processCompressorsData(room.compressorData).map(compressor => omit(compressor, 'images'))
          : [],
        tanks: Array.isArray(room.tanks) ? [...room.tanks] : [],
        dryers: Array.isArray(room.dryers) ? [...room.dryers] : [],
      };

      const roomTankForm = room.newTank;
      if (roomTankForm) {
        roomDataObj.tanks.push({
          tankType: roomTankForm.tankType as TanksType,
          tankValue: roomTankForm.tankValue,
        });
      }

      const roomDryerForm = room.newDryer;
      if (roomDryerForm) {
        roomDataObj.dryers.push({
          dryerType: roomDryerForm.dryerType,
        });
      }

      roomDataObj.tanks =
        roomDataObj.tanks?.map(tank => {
          if (tank && tank.tankType) {
            return {
              ...tank,
              tankValue: convertEnteredValue(tank?.tankValue || 0, 'gallon'),
            };
          }
          return tank;
        }) || [];

      roomsData.push(roomDataObj);
    }
  });

  return roomsData;
};

const processCompressorsData = (compressorData: SaveCompressorDataPayload[]) => {
  const resultCompressorData: SaveCompressorDataPayload[] = [];

  if (compressorData && Array.isArray(compressorData) && compressorData.length > 0) {
    compressorData.forEach(compressor => {
      if (compressor.modelSpec === NO_MATCHING_SPEC) {
        const pressure = compressor.additionalInformation?.pressure;
        const convertedPressure = pressure != null ? convertEnteredValue(pressure, 'psi') : undefined;

        resultCompressorData.push({
          ...compressor,
          additionalInformation: {
            ...(compressor.additionalInformation || {}),
            pressure: convertedPressure,
          },
        });
      } else {
        resultCompressorData.push(compressor);
      }
    });
  }
  return resultCompressorData;
};

const convertEnteredValue = (value: number, unit: string) => {
  const userMeasurementSystem = rootStore.userStore.user?.measurementSystem || 'METRIC';
  return convertValue(value, unit, 'METRIC', userMeasurementSystem);
};

export const prepareRoomConfigurationStepPayload = async (
  customerInformationStepData: CustomerInformationStepData,
  siteConfigurationStepData: SiteConfigurationStepData,
  roomConfigurationStepData: Partial<RoomConfigurationStepData>,
  accountData: AccountResponse | null,
  formId: string | null,
): Promise<SaveOnboardingFormPayload> => {
  if (!formId) {
    throw new Error('No form ID available. Please save Step 1 data first.');
  }

  if (
    !roomConfigurationStepData ||
    typeof roomConfigurationStepData !== 'object' ||
    Object.keys(roomConfigurationStepData).length === 0
  ) {
    throw new Error('No assets data available');
  }

  const { firstName, lastName } = splitFullName(customerInformationStepData.contactPerson.fullName || '');
  const { firstName: accountManagerFirstName, lastName: accountManagerLastName } = splitFullName(
    customerInformationStepData.accountManager?.fullName ?? '',
  );
  const pressureType = siteConfigurationStepData.pressureType || PressureTypeFull.MAIN;
  const roomsData = processRoomsData(roomConfigurationStepData, pressureType);

  const siteGeneralConfiguration = convertSiteConfigurationStepDataToDTO(siteConfigurationStepData);
  let images: CreateImagePayload[] = [];

  if (roomsData.length > 0) {
    siteGeneralConfiguration.roomsData = roomsData;
    images = await processImagesData(roomConfigurationStepData, pressureType);
  }

  return {
    salesforceAccountId: customerInformationStepData.customer?.accountId || '',
    formId,
    siteData: {
      generalData: {
        siteAddress: {
          streetAddress: customerInformationStepData.siteAddress.streetAddress || '',
          city: customerInformationStepData.siteAddress.city || '',
          state: customerInformationStepData.siteAddress.state || '',
          country: customerInformationStepData.siteAddress.country || '',
          zipCode: customerInformationStepData.siteAddress.zipCode || '',
        },
        contactPerson: {
          firstName,
          lastName,
          email: customerInformationStepData.contactPerson.email || '',
        },
        accountManager: {
          firstName: accountManagerFirstName,
          lastName: accountManagerLastName,
          email: customerInformationStepData.accountManager?.email ?? '',
        },
        stationTier: customerInformationStepData.tier || '',
        stationUsageType: customerInformationStepData.stationUsageType || STATION_USAGE_TYPES.REGULAR,
        ecoplantCustomerId: customerInformationStepData.account || null,
        customerName: accountData?.customerName || customerInformationStepData.newCustomerName || '',
        channelIdentifier: accountData?.cgOrgChannelC || '',
      },
      siteGeneralConfiguration,
    },
    images,
  };
};

export const processImagesData = async (
  roomConfigurationStepData: Partial<RoomConfigurationStepData>,
  pressureType: string,
): Promise<CreateImagePayload[]> => {
  const pressureTypeData = roomConfigurationStepData[pressureType] as PressureTypeRoomData;
  const rooms = pressureTypeData?.rooms;

  if (!rooms.length) {
    return [];
  }

  const compressors = rooms.flatMap(({ compressorData }) => compressorData);

  if (!compressors?.length) {
    return [];
  }

  const images = await Promise.all(compressors.flatMap(extractImagesFromCompressor));

  return images.flat();
};

const extractImagesFromCompressor = async (compressor: CompressorData): Promise<CreateImagePayload[]> => {
  if (!compressor.images?.length) {
    return [];
  }

  const imagesPayload = compressor.images.map(image => mapImageDataToPayload(image, compressor));

  return await Promise.all(imagesPayload);
};

const mapImageDataToPayload = async (image: ImageData, compressor: CompressorData): Promise<CreateImagePayload> => ({
  id: uuidv4(),
  serial: compressor.serialNumber,
  tag: image.tag,
  count: image.id,
  file: await imageToFile(image),
});

const imageToFile = async (image: ImageData): Promise<File> => {
  if (!image.url) {
    throw new Error('No image URL available');
  }

  const response = await fetch(image.url);
  const blob = await response.blob();
  const fileName = image.originalName || `${image.id}.${blob.type.split('/')[1]}`;

  return new File([blob], fileName, { type: blob.type });
};
